import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type CaseStatus = 'active' | 'closed' | 'archived'
export type CaseType = 'investigation' | 'surveillance' | 'incident' | 'other'
export type CasePriority = 'low' | 'medium' | 'high' | 'critical'
export type VideoStatus = 'pending' | 'uploading' | 'processing' | 'ready' | 'failed'

export type EditableCaseFields = Pick<Case, 'name' | 'description' | 'type' | 'status' | 'priority' | 'location' | 'incidentDate' | 'assignedTo' | 'notes'>

export interface Case {
  id: string
  caseNumber: string
  name: string
  description: string
  type: CaseType
  status: CaseStatus
  priority: CasePriority
  location: string
  incidentDate: string
  assignedTo: string
  notes: string
  createdAt: string
  updatedAt: string
  createdBy: string
  videoCount: number
}

export interface Video {
  id: string
  caseId: string
  fileName: string
  fileSize: number
  status: VideoStatus
  uploadedAt: string
  uploadedBy: string
}

interface CaseStore {
  cases: Case[]
  videos: Video[]
  selectedCaseId: string | null
  addCase: (name: string, description: string, type: CaseType, userId: string) => string
  updateCase: (caseId: string, updates: Partial<EditableCaseFields>) => void
  selectCase: (caseId: string) => void
  addVideo: (caseId: string, fileName: string, fileSize: number, userId: string) => void
  getVideosForCase: (caseId: string) => Video[]
}

const generateCaseNumber = (count: number): string => {
  const year = new Date().getFullYear()
  const sequence = String(count + 1).padStart(3, '0')
  return `CASE-${year}-${sequence}`
}

export const useCaseStore = create<CaseStore>()(
  persist(
    (set, get) => ({
      cases: [],
      videos: [],
      selectedCaseId: null,

      addCase: (name: string, description: string, type: CaseType, userId: string) => {
        const caseId = crypto.randomUUID()
        const now = new Date().toISOString()
        const caseNumber = generateCaseNumber(get().cases.length)
        
        const newCase: Case = {
          id: caseId,
          caseNumber,
          name,
          description,
          type,
          status: 'active',
          priority: 'medium',
          location: '',
          incidentDate: '',
          assignedTo: '',
          notes: '',
          createdAt: now,
          updatedAt: now,
          createdBy: userId,
          videoCount: 0,
        }
        
        set((state) => ({
          cases: [...state.cases, newCase],
        }))
        
        return caseId
      },

      selectCase: (caseId: string) => {
        set({ selectedCaseId: caseId })
      },

      updateCase: (caseId: string, updates) => {
        const now = new Date().toISOString()
        set((state) => ({
          cases: state.cases.map((c) =>
            c.id === caseId ? { ...c, ...updates, updatedAt: now } : c
          ),
        }))
      },

      addVideo: (caseId: string, fileName: string, fileSize: number, userId: string) => {
        const videoId = crypto.randomUUID()
        const now = new Date().toISOString()
        
        const newVideo: Video = {
          id: videoId,
          caseId,
          fileName,
          fileSize,
          status: 'pending',
          uploadedAt: now,
          uploadedBy: userId,
        }
        
        set((state) => ({
          videos: [...state.videos, newVideo],
          cases: state.cases.map((c) =>
            c.id === caseId
              ? { ...c, videoCount: c.videoCount + 1, updatedAt: now }
              : c
          ),
        }))
      },

      getVideosForCase: (caseId: string) => {
        return get().videos.filter((v) => v.caseId === caseId)
      },
    }),
    {
      name: 'librarian-cases',
    }
  )
)
